/** Client-side enrichment job placeholders (localStorage), cleared on DB reset. */



export const PENDING_JOBS_KEY = "pending_jobs";

function readCurrentUserId() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return String(u?.id || u?.user_id || "").trim();
  } catch {
    return "";
  }
}

function pendingJobOwnerId(job) {
  const fromPayload =
    job?.payload && typeof job.payload === "object"
      ? job.payload.queued_by_user_id
      : null;
  return String(job?.queued_by_user_id || fromPayload || "").trim();
}

function pendingJobsForCurrentUser(jobs) {
  const actor = readCurrentUserId();
  if (!actor) return [];
  return (Array.isArray(jobs) ? jobs : []).filter(
    (job) => pendingJobOwnerId(job) === actor
  );
}

export const AI_PROCESSING_KEY = "ai_processing";



export function isEnrichmentJobType(job) {
  const t = String(job?.job_type || "").toLowerCase();
  return (
    t === "application_enrichment" ||
    t === "entity_refine" ||
    t === "knowledge_ingestion"
  );
}



const MAX_PENDING = 5;



function safeParsePending() {

  try {

    const pending = JSON.parse(localStorage.getItem(PENDING_JOBS_KEY) || "[]");

    return pendingJobsForCurrentUser(Array.isArray(pending) ? pending : []);

  } catch {

    return [];

  }

}



function writePending(jobs) {

  try {

    const scoped = pendingJobsForCurrentUser(jobs).slice(-MAX_PENDING);

    localStorage.setItem(PENDING_JOBS_KEY, JSON.stringify(scoped));

  } catch {

    /* ignore */

  }

}



export function readPendingEnrichmentJob(jobId) {

  const id = String(jobId || "").trim();

  if (!id) return null;

  return (

    safeParsePending().find(

      (j) => isEnrichmentJobType(j) && String(j?.id || "").trim() === id

    ) || null

  );

}



/** Set global AI-processing flag and notify shell + inbox (synchronous). */

export function signalEnrichmentProcessingActive() {

  try {

    const current = localStorage.getItem(AI_PROCESSING_KEY) || "0";

    if (current !== "1") {

      localStorage.setItem(AI_PROCESSING_KEY, "1");

      window.dispatchEvent(new CustomEvent("aiProcessingStart"));

    }

  } catch {

    /* ignore */

  }

}



export function createOptimisticEnrichmentProvisionalId() {

  return `opt_submit_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

}



export function addOptimisticEnrichmentJob(job) {

  const id = String(job?.id || "").trim();

  if (!id) return job;

  const pending = safeParsePending().filter((j) => String(j?.id || "").trim() !== id);

  pending.push(job);

  writePending(pending);

  return job;

}



export function removeOptimisticEnrichmentJob(jobId) {

  const id = String(jobId || "").trim();

  if (!id) return;

  const pending = safeParsePending().filter((j) => String(j?.id || "").trim() !== id);

  writePending(pending);

}



function enrichmentJobDisplayName(anchorName, response) {

  const fromApi = String(response?.job_display_name || "").trim();

  if (fromApi) return fromApi;

  const anchor = String(anchorName || "").trim();

  if (anchor) return anchor;

  return "Knowledge upload";

}



function buildOptimisticEnrichmentRow({

  id,

  status = "pending",

  anchorName,

  anchorApplicationId,

  response,

  isOptimistic = true,

}) {

  const now = new Date().toISOString();

  const anchorId = String(anchorApplicationId || "").trim();

  const ownerId = readCurrentUserId();

  return {

    id: String(id || "").trim(),

    job_type: String(response?.job_type || "application_enrichment").toLowerCase() || "application_enrichment",

    status: String(status || "pending"),

    created_at: now,

    updated_at: now,

    is_optimistic: Boolean(isOptimistic),

    name: enrichmentJobDisplayName(anchorName, response),

    ...(ownerId ? { queued_by_user_id: ownerId } : {}),

    ...(anchorId ? { anchor_application_id: anchorId } : {}),

  };

}



function dispatchEnrichmentQueueEvents(jobId) {

  try {

    window.dispatchEvent(

      new CustomEvent("newJobQueued", {

        detail: { jobId: String(jobId || "").trim() },

      })

    );

  } catch {

    /* ignore */

  }

}



/**

 * Show "AI Processing in progress" and an Inbox row before the queue API returns.

 * Also sets ai_processing and dispatches aiProcessingStart + newJobQueued.

 */

export function beginOptimisticEnrichmentQueue({

  provisionalId = null,

  anchorName = "",

  anchorApplicationId = "",

} = {}) {

  const id = String(provisionalId || createOptimisticEnrichmentProvisionalId()).trim();

  const row = buildOptimisticEnrichmentRow({

    id,

    anchorName,

    anchorApplicationId,

  });

  addOptimisticEnrichmentJob(row);

  signalEnrichmentProcessingActive();

  dispatchEnrichmentQueueEvents(id);

  return id;

}



function serverEnrichmentJobId(response = {}) {

  return String(response?.job_id || response?.jobId || "").trim();

}



export function isEnrichmentQueueTimeoutError(error) {

  const msg = String(error?.message || error || "").toLowerCase();

  return (

    msg.includes("timeout") ||

    error?.code === "ECONNABORTED" ||

    String(error?.response?.status || "") === "408"

  );

}



export function createOptimisticEnrichmentTimeoutId() {

  return `opt_timeout_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

}



/** Replace the provisional row with the server job id (or a client placeholder). */

export function finalizeOptimisticEnrichmentQueue(

  provisionalId,

  response = {},

  { anchorName = "", anchorApplicationId = "" } = {}

) {

  if (provisionalId) {

    removeOptimisticEnrichmentJob(provisionalId);

  }

  const serverId = serverEnrichmentJobId(response);

  const jobId = serverId || `opt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const row = buildOptimisticEnrichmentRow({

    id: jobId,

    status: response?.status || "pending",

    anchorName,

    anchorApplicationId,

    response,

    isOptimistic: !serverId,

  });

  addOptimisticEnrichmentJob(row);

  if (serverId) {

    try {

      localStorage.setItem("activeJobId", jobId);

    } catch {

      /* ignore */

    }

  }

  signalEnrichmentProcessingActive();

  dispatchEnrichmentQueueEvents(jobId);

  return jobId;

}



/**

 * Queue API timed out but the job may still have been created — keep banner/inbox active.

 */

export function finalizeOptimisticEnrichmentQueueOnTimeout(

  provisionalId,

  { anchorName = "", anchorApplicationId = "" } = {}

) {

  if (provisionalId) {

    removeOptimisticEnrichmentJob(provisionalId);

  }

  const jobId = createOptimisticEnrichmentTimeoutId();

  addOptimisticEnrichmentJob(

    buildOptimisticEnrichmentRow({

      id: jobId,

      anchorName,

      anchorApplicationId,

    })

  );

  signalEnrichmentProcessingActive();

  dispatchEnrichmentQueueEvents(jobId);

  return jobId;

}



/** Remove provisional/placeholder rows after a failed queue attempt and refresh inbox state. */

export function rollbackOptimisticEnrichmentQueue(...jobIds) {

  for (const raw of jobIds) {

    const id = String(raw || "").trim();

    if (id) removeOptimisticEnrichmentJob(id);

  }

  try {

    window.dispatchEvent(new CustomEvent("newJobQueued", { detail: {} }));

  } catch {

    /* ignore */

  }

}



/**

 * Idempotent: ensure banner + inbox show a queued enrichment row when the UI

 * displays "Application enrichment is queued from your upload…".

 */

export function ensureEnrichmentQueueVisibleFromUpload({

  anchorName = "",

  anchorApplicationId = "",

  jobId = "",

} = {}) {

  const anchorId = String(anchorApplicationId || "").trim();

  const resolvedJobId = String(jobId || "").trim();

  let existing = null;

  if (resolvedJobId) {

    existing = readPendingEnrichmentJob(resolvedJobId);

  }

  if (!existing && anchorId) {

    const pending = safeParsePending().filter(isEnrichmentJobType);

    existing =

      pending.find(

        (j) => String(j?.anchor_application_id || "").trim() === anchorId

      ) || pending[pending.length - 1];

  }

  if (!existing) {

    beginOptimisticEnrichmentQueue({ anchorName, anchorApplicationId: anchorId });

    return;

  }

  signalEnrichmentProcessingActive();

  dispatchEnrichmentQueueEvents(existing.id);

}



/** Drop enrichment optimistic rows and active job pointer (e.g. after clear database). */

export function clearEnrichmentClientJobCache() {

  try {

    localStorage.removeItem("activeJobId");

  } catch {

    /* ignore */

  }

  try {

    const pending = JSON.parse(localStorage.getItem(PENDING_JOBS_KEY) || "[]");

    if (!Array.isArray(pending)) {

      localStorage.setItem(PENDING_JOBS_KEY, "[]");

      return;

    }

    const remaining = pending.filter((j) => !isEnrichmentJobType(j));

    if (remaining.length !== pending.length) {

      localStorage.setItem(PENDING_JOBS_KEY, JSON.stringify(remaining));

    }

  } catch {

    /* ignore */

  }

}


